import { DEFAULT_DISPLAY_THEME } from 'config/theme.config';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserProfileInfo, updateUserProfileOptions } from 'services/user/user.service';
import { NAVIGATION_STYLES } from 'store/config/displayOptions.config';
import {
  selectAccessibilityOptions,
  selectChartOptions,
  selectMapOptions,
  selectNavigationStyle,
} from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import {
  setAccessibilityColorMode,
  setChartStyle,
  setMapStyle,
  setNavigationStyle,
} from 'store/slices/DisplayOptions/displayOptionsSlice';
import {
  mergeProfileDisplayOptions,
  normalizeDisplayOptionsForProfile,
} from 'utils/preferences/userOptions.utils';
import AccessibilityForm from './components/AccessibilityForm/AccessibilityForm';
import AccessibilitySetting from './components/AccessibilitySetting/AccessibilitySetting';
import ChartPreview from './components/ChartPreview/ChartPreview';
import MapPreview from './components/MapPreview/MapPreview';
import {
  chartStyleOptions,
  colorModeOptions,
  customThemeStyleOption,
  mapStyleOptions,
  navigationStyleOptions,
} from './config/accessibilitySettings.config';
import './assets/accessibility.scss';

/** Apply display preferences immediately and save the selected settings to the profile. */
const Accessibility = ({ appearance }) => {
  const dispatch = useDispatch();
  const mapOptions = useSelector(selectMapOptions);
  const chartOptions = useSelector(selectChartOptions);
  const accessibilityOptions = useSelector(selectAccessibilityOptions);
  const navigationStyle = useSelector(selectNavigationStyle);
  const isClassic = (appearance ?? navigationStyle) === NAVIGATION_STYLES.CLASSIC;
  const [saveStatus, setSaveStatus] = useState('idle');
  const saveInProgress = useRef(false);
  const navigationInputId = useId();
  const previousNavigationStyle = useRef(navigationStyle);
  const normalizedDisplayOptions = useMemo(
    () =>
      normalizeDisplayOptionsForProfile({
        displayTheme: DEFAULT_DISPLAY_THEME,
        mapOptions,
        chartOptions,
        accessibilityOptions,
      }),
    [accessibilityOptions, chartOptions, mapOptions],
  );
  const { mapOptions: normalizedMapOptions, chartOptions: normalizedChartOptions } =
    normalizedDisplayOptions;
  const selectedThemeStyle =
    normalizedMapOptions.mapStyle === normalizedChartOptions.chartStyle
      ? normalizedMapOptions.mapStyle
      : customThemeStyleOption.value;
  const themeStyleOptions =
    selectedThemeStyle === customThemeStyleOption.value
      ? [customThemeStyleOption, ...mapStyleOptions]
      : mapStyleOptions;
  const serializedDisplayOptions = JSON.stringify(normalizedDisplayOptions);
  const lastSavedDisplayOptions = useRef(serializedDisplayOptions);

  // Keep Navigation focused after its selection changes the surrounding appearance.
  useEffect(() => {
    if (previousNavigationStyle.current === navigationStyle) return;
    previousNavigationStyle.current = navigationStyle;
    document.getElementById(navigationInputId)?.focus();
  }, [navigationInputId, navigationStyle]);

  useEffect(() => {
    if (saveStatus === 'saved' && lastSavedDisplayOptions.current !== serializedDisplayOptions) {
      setSaveStatus('idle');
    }
  }, [saveStatus, serializedDisplayOptions]);

  const changeThemeStyle = (style) => {
    dispatch(setMapStyle(style));
    dispatch(setChartStyle(style));
  };

  const saveSettings = async () => {
    if (saveInProgress.current) return;
    saveInProgress.current = true;
    setSaveStatus('saving');

    try {
      // Read the latest profile before merging so unrelated saved options stay intact.
      const response = await getUserProfileInfo();
      const profile = response?.data?.result;

      if (!profile || typeof profile !== 'object' || Array.isArray(profile)) {
        setSaveStatus('error');
        return;
      }

      const nextProfileOptions = mergeProfileDisplayOptions(
        profile.options,
        normalizedDisplayOptions,
      );
      const saveResponse = await updateUserProfileOptions(nextProfileOptions);

      if (saveResponse?.status >= 200 && saveResponse?.status < 300) {
        lastSavedDisplayOptions.current = serializedDisplayOptions;
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    } finally {
      saveInProgress.current = false;
    }
  };

  return (
    <AccessibilityForm
      isClassic={isClassic}
      controls={{
        theme: (
          <AccessibilitySetting
            label="Theme"
            options={themeStyleOptions}
            value={selectedThemeStyle}
            isOptionDisabled={(option) => option.value === customThemeStyleOption.value}
            onChange={changeThemeStyle}
          />
        ),
        // Navigation is a device preference and is deliberately excluded from profile saves.
        navigation: (
          <AccessibilitySetting
            inputId={navigationInputId}
            label="Navigation"
            options={navigationStyleOptions}
            value={navigationStyle}
            onChange={(value) => dispatch(setNavigationStyle(value))}
          />
        ),
        colorMode: (
          <AccessibilitySetting
            label="Color Mode"
            ariaLabel="Color Accessibility Mode"
            options={colorModeOptions}
            value={accessibilityOptions.colorMode}
            onChange={(value) => dispatch(setAccessibilityColorMode(value))}
          />
        ),
        mapStyle: (
          <AccessibilitySetting
            label="Map Style"
            options={mapStyleOptions}
            value={normalizedMapOptions.mapStyle}
            onChange={(value) => dispatch(setMapStyle(value))}
          />
        ),
        chartStyle: (
          <AccessibilitySetting
            label="Chart Style"
            options={chartStyleOptions}
            value={normalizedChartOptions.chartStyle}
            onChange={(value) => dispatch(setChartStyle(value))}
          />
        ),
      }}
      mapPreview={<MapPreview mapOptions={normalizedMapOptions} />}
      chartPreview={<ChartPreview chartOptions={normalizedChartOptions} />}
      saveStatus={saveStatus}
      onSave={saveSettings}
    />
  );
};

export default Accessibility;
